import { useCallback, useEffect, useState } from "react";

import copyToClipboard from "copy-to-clipboard";
import { useMediaQuery } from "react-responsive";

import { useHistory } from "react-router-dom";

import { merge } from "rxjs";

import ROUTES from "manager/config/routes";
import useUnblockApplicationItem from "manager/hooks/useUnblockApplicationItem";
import { BREAKPOINT_RESOLUTIONS } from "shared/config/constants";
import { transformRoute } from "shared/utils/routing";
import { notifyInfo } from "shared/utils/ui";

import { AppRowProps } from "./app-row";
import { ApplicantInfoProps } from "./applicant-info";
import {
  useRemoveApplicantModal,
  useMoveApplicantModal,
  RemoveApplicantModalProps,
} from "./modals";

export const useAppRow = ({
  dealId,
  appId,
  info,
  yardi,
  refetchDeal,
}: Pick<
  AppRowProps,
  "appId" | "info" | "dealId" | "refetchDeal" | "yardi"
>) => {
  const isMobile = useMediaQuery({ maxWidth: BREAKPOINT_RESOLUTIONS.sm });
  const history = useHistory();
  const [collapse, setCollapse] = useState(false);

  useEffect(() => {
    setCollapse(isMobile);
  }, [isMobile]);

  const toggle = useCallback(
    () => isMobile && setCollapse(!collapse),
    [isMobile, collapse]
  );

  const onCopyAppLink = () => {
    if (info?.inviteLink) {
      copyToClipboard(info.inviteLink);
      notifyInfo("Application link copied to clipboard!");
    }
  };

  const onIncomeClick = () => {
    history.push(
      transformRoute(ROUTES.applicationIncomeAssets, {
        id: appId,
      })
    );
  };
  const onCreditBackgroundClick = () => {
    history.push(
      transformRoute(ROUTES.applicationCreditBackground, {
        id: appId,
      })
    );
  };
  const onVerificationClick = () => {
    history.push(
      transformRoute(ROUTES.applicationVerifications, {
        id: appId,
      })
    );
  };
  const onVorVerificationClick = () => {
    history.push(
      transformRoute(ROUTES.applicationVorVerifications, {
        id: appId,
      })
    );
  };

  const onEmploymentClick = () => {
    history.push(
      transformRoute(ROUTES.applicationRenterProfile, {
        id: appId,
      })
    );
  };

  const { show: moveApplicant, ok$: onMovedApplicant$ } =
    useMoveApplicantModal();

  const { show: removeApplicant, ok$: onRemovedApplicant$ } =
    useRemoveApplicantModal();

  const viewAttachments = (appId: string | number) => {
    history.push(
      transformRoute(ROUTES.applicationAttachments, {
        id: appId,
      })
    );
  };
  const viewApplication = (appId: string | number) => {
    history.push(
      transformRoute(ROUTES.application, {
        id: appId,
      })
    );
  };

  const infoProps: ApplicantInfoProps = {
    ...(info || {}),
    applicationId: info?.applicationId || appId,
    name: info?.name || "",
    isSubmitted: info?.isSubmitted || false,
    isApproved: info?.isApproved || false,
    email: info?.email || "",
    phone: info?.phone || "",
    onDelete: () => removeApplicant(),
    onViewAttachments: () => viewAttachments(appId),
    onMove: () => moveApplicant(),
    onViewApp: () => viewApplication(appId),
  };

  const unblock = useUnblockApplicationItem(
    {
      id: info?.applicationId ? +info.applicationId : 0,
      firstName: info?.name || "",
      lastName: "",
      isSubmitted: info?.isSubmitted || false,
      blockDealSubmitIfIncomeDataIsMissing: info?.isBlocked || false,
      canUpdateBlockDealSubmitIfIncomeDataIsMissing: {
        value: info?.permission?.unblock || false,
      },
    },
    refetchDeal
  );

  merge(onMovedApplicant$, onRemovedApplicant$).subscribe(() => {
    refetchDeal();
  });

  const modalProps: RemoveApplicantModalProps = {
    application: {
      firstName: info?.name || "",
      lastName: "",
      id: appId,
      isSubmitted: info?.isSubmitted || false,
    },
    dealId,
    yardi: yardi || { isPrimary: false },
  };

  return {
    infoProps,
    onIncomeClick,
    onCreditBackgroundClick,
    onVerificationClick,
    onVorVerificationClick,
    onEmploymentClick,
    toggle,
    collapse,
    onCopyAppLink,
    isMobile,
    unblock,
    modalProps,
  };
};
